import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  User,
  AlertCircle,
  MapPin,
  X,
  PlayCircle,
  XCircle,
  UserPlus,
} from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../hooks/useAuth";
import { AppointmentStatusBadge } from "./components/AppointmentStatusBadge";
import { ReassignModal } from "../dashboard/receptionist/modals/ReassignModal";


export const AppointmentDetails = ({ appointmentId, onClose }) => {
  const { get, post, put } = useApi();
  const { user } = useAuth();
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);

  // Use either the prop appointmentId or the URL param id
  const id = appointmentId || urlId;

  useEffect(() => {
    const fetchAppointmentDetails = async () => {
      try {
        setLoading(true);
        const response = await get(`/appointments/${id}`);
        if (response.data?.status === "success") {
          setAppointment(response.data.data);
        } else {
          setError("Failed to load appointment details");
        }
      } catch (err) {
        console.error("Error fetching appointment:", err);
        setError(
          err.response?.data?.message || "Failed to load appointment details"
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAppointmentDetails();
    }
  }, [id, get]);

  const handleStartSession = async () => {
    try {
      setIsStartingSession(true);
      setError(null);

      // First update appointment status
      const statusResponse = await put(
        `/appointments/${appointment._id}/status`,
        { status: 'in-progress' }
      );

      if (statusResponse.data?.status === 'success') {
        // Initialize the session
        const sessionResponse = await post(
          `/sessions/${appointment._id}/initialize`
        );

        if (sessionResponse.data?.status === 'success') {
          // Use the appropriate route based on user role
          if (user.role === 'hod') {
            navigate(`/hod/sessions/${appointment._id}`);
          } else {
            navigate(`/counselor/sessions/${appointment._id}`);
          }
        } else {
          throw new Error('Failed to initialize session');
        }
      } else {
        throw new Error('Failed to update appointment status');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      setError(error.response?.data?.message || 'Failed to start session');
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) {
      return;
    }

    try {
      await post(`/appointments/${id}/cancel`);
      if (onClose) {
        onClose();
      } else {
        navigate("/appointments");
      }
    } catch (error) {
      console.error("Failed to cancel appointment:", error);
      setError("Failed to cancel appointment. Please try again.");
    }
  };

  const handleReassign = async (appointmentId, reassignData) => {
    try {
      setError(null);
      const response = await put(`/appointments/${appointmentId}/reassign`, reassignData);

      if (response.data?.status === "success") {
        setAppointment(response.data.data);
        setShowReassignModal(false);
      } else {
        throw new Error("Failed to reassign appointment");
      }
    } catch (error) {
      console.error("Failed to reassign appointment:", error);
      setError(error.response?.data?.message || "Failed to reassign appointment");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Appointment not found</p>
      </div>
    );
  }

  const canStartSession =
    (user?.role === "counselor") &&
    appointment?.status === "scheduled" &&
    appointment?.counselor?._id === user?._id;

  const canCancelAppointment =
    user.role === "student" ||
    user.role === "counselor" || user.role === "staff" ||
    user.role === "receptionist";

  const canReschedule =
    (user.role === "student" && appointment.client._id === user.id) ||
    user.role === "receptionist" || user.role === "staff";

  const canReassign =
    user.role === "receptionist" ||
    user.role === "admin" ||
    user.role === "staff";

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-2xl mx-auto">
      {/* Header */}
      <div className="px-6 py-4 border-b flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">
          Appointment Details
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="max-h-[70vh] overflow-y-auto p-6">
        {/* Status Badge */}
        <div className="mb-6 flex justify-between items-center">
          <AppointmentStatusBadge status={appointment.status} />
          <span className="text-sm text-gray-500 mr-4">
            Student ID:  {appointment.client?.studentInfo?.studentId}
          </span>
        </div>

        {/* Appointment Info */}
        <div className="space-y-6">
          {/* Client Info (visible to counselors and admin) */}
          {(user.role === "counselor" ||
            user.role === "admin" ||
            user.role === "receptionist") && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Client</h3>
                <div className="flex items-center">
                  <User className="h-5 w-5 text-gray-400 mr-2" />
                  <p className="text-gray-900">

                    {appointment.client?.fullName ||
                      appointment.client?.username}
                  </p>
                </div>
              </div>
            )}



          {/* Service & Counselor */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Service</h3>
            <p className="text-gray-900 mb-6">{appointment.serviceType}</p>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Session Type</h3>
            <p className="text-gray-900">{appointment.sessionType}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">
              Counselor
            </h3>
            <div className="flex items-center">
              <User className="h-5 w-5 text-gray-400 mr-2" />
              <p className="text-gray-900">
                {appointment.counselor?.fullName ||
                  appointment.counselor?.username}
              </p>
            </div>
          </div>

          {/* Client Details */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Client Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><strong className="text-sm font-medium text-gray-500">Full Name:</strong> {appointment.client?.fullName}</div>
              <div><strong className="text-sm font-medium text-gray-500">Email:</strong> {appointment.client?.email}</div>
              <div><strong className="text-sm font-medium text-gray-500">Username:</strong> {appointment.client?.username}</div>
              <div><strong className="text-sm font-medium text-gray-500">Phone Number:</strong> {appointment.client?.phone}</div>
            </div>

            {/* Student Info */}
            {appointment.client?.studentInfo && (
              <>
                {/* Convert the intake semesters to the actual values */}
                <h4 className="text-md font-medium text-gray-600 mt-6 mb-2">Student Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><strong className="text-sm font-medium text-gray-500">Student ID:</strong> {appointment.client.studentInfo.studentId}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Enrollment Year:</strong> {appointment.client.studentInfo.enrollmentYear}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Intake Semester:</strong> {appointment.client.studentInfo.intakeSemester}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Year of Study:</strong> {appointment.client.calculatedYearOfStudy || appointment.client.studentInfo.yearOfStudy}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Major:</strong> {appointment.client.studentInfo.major}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Gender:</strong> {appointment.client.studentInfo.profile?.gender}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Date of Birth:</strong> {new Date(appointment.client.studentInfo.profile?.dateOfBirth).toLocaleDateString()}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Nationality:</strong> {appointment.client.studentInfo.profile?.nationality}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Religion:</strong> {appointment.client.studentInfo.profile?.religion}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Residence:</strong> {appointment.client.studentInfo.profile?.residence}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Marital Status:</strong> {appointment.client.studentInfo.profile?.maritalStatus}</div>
                </div>

                <h4 className="text-md font-medium text-gray-600 mt-6 mb-2">Next of Kin</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><strong className="text-sm font-medium text-gray-500">Name:</strong> {appointment.client.studentInfo.nextOfKin?.name}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Relation:</strong> {appointment.client.studentInfo.nextOfKin?.relation}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Email:</strong> {appointment.client.studentInfo.nextOfKin?.email}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Phone:</strong> {appointment.client.studentInfo.nextOfKin?.phone}</div>
                </div>
              </>
            )}

            {/* Staff Info */}
            {appointment.client?.staffInfo && (
              <>
                <h4 className="text-md font-medium text-gray-600 mt-6 mb-2">Staff Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><strong className="text-sm font-medium text-gray-500">Staff ID:</strong> {appointment.client.staffInfo.staffId}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Department:</strong> {appointment.client.staffInfo.department}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Position:</strong> {appointment.client.staffInfo.position}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Gender:</strong> {appointment.client.staffInfo.profile?.gender}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Date of Birth:</strong> {new Date(appointment.client.staffInfo.profile?.dateOfBirth).toLocaleDateString()}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Nationality:</strong> {appointment.client.staffInfo.profile?.nationality}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Religion:</strong> {appointment.client.staffInfo.profile?.religion}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Residence:</strong> {appointment.client.staffInfo.profile?.residence}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Marital Status:</strong> {appointment.client.staffInfo.profile?.maritalStatus}</div>
                </div>

                <h4 className="text-md font-medium text-gray-600 mt-6 mb-2">Next of Kin</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><strong className="text-sm font-medium text-gray-500">Name:</strong> {appointment.client.staffInfo.nextOfKin?.name}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Relation:</strong> {appointment.client.staffInfo.nextOfKin?.relation}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Email:</strong> {appointment.client.staffInfo.nextOfKin?.email}</div>
                  <div><strong className="text-sm font-medium text-gray-500">Phone:</strong> {appointment.client.staffInfo.nextOfKin?.phone}</div>
                </div>
              </>
            )}
          </div>


          {/* Date & Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Date</h3>
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                <p className="text-gray-900">
                  {new Date(appointment.date).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Time</h3>
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-gray-400 mr-2" />
                <p className="text-gray-900">{appointment.timeSlot}</p>
              </div>
            </div>
          </div>

          {/* Location/Notes */}
          {appointment.location && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Location
              </h3>
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                <p className="text-gray-900">{appointment.location}</p>
              </div>
            </div>
          )}

          {appointment.notes && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
              <p className="text-gray-900 whitespace-pre-wrap">
                {appointment.notes}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        {appointment.status === "scheduled" && (
          <div className="mt-8 flex justify-end space-x-3">
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            )}

            {canCancelAppointment && (
              <button
                onClick={handleCancelAppointment}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center"
              >
                <XCircle className="h-5 w-5 mr-2" />
                Cancel Appointment
              </button>
            )}

            {canReassign && (
              <button
                onClick={() => setShowReassignModal(true)}
                className="px-4 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-600 flex items-center"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Reassign
              </button>
            )}

            {canReschedule && (
              <button
                onClick={() =>
                  navigate(`/appointments/${appointment._id}/reschedule`)
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Reschedule
              </button>
            )}

            {canStartSession && (
              <button
                onClick={handleStartSession}
                disabled={isStartingSession}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center disabled:bg-green-400"
              >
                {isStartingSession ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Starting Session...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-5 w-5 mr-2" />
                    Start Session
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Reassign Modal */}
      <ReassignModal
        isOpen={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        appointment={appointment}
        onReassign={handleReassign}
      />
    </div>
  );
};

export default AppointmentDetails;

